import React, { useState, useCallback } from "react";
import { BaseModal } from "@/components/ui/base-modal";
import { Input, Select, Option, Switch, Slider, Typography } from "@/components/ui";
import { FaPlay, FaClock, FaListOl, FaCalendarAlt, FaRocket, FaCog } from "react-icons/fa";
import { Tracker } from "@/models/tracker";
import { getTrackerValue, getTrackerSource } from "@/utils/tracker-helpers";
import { SourceIcon } from "@/components/resource-card";

/** Options for running a tracker */
export interface RunTrackerOptions {
  /** Maximum number of results to fetch */
  limit?: number;
  /** Time period for the search (in days) */
  periodDays?: number;
  /** Custom start date */
  fromDate?: string;
  /** Custom end date */
  toDate?: string;
  /** Force execution even if recently run */
  force?: boolean;
  /** Run asynchronously */
  runAsync?: boolean;
}

interface RunTrackerModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when the modal is closed */
  onClose: () => void;
  /** The tracker to run */
  tracker: Tracker | null;
  /** Callback when the user confirms execution */
  onConfirm: (options: RunTrackerOptions) => void;
  /** Whether the execution is in progress */
  isLoading?: boolean;
}

/** Preset period options */
const PERIOD_PRESETS = [
  { value: "1", label: "Dernières 24h" },
  { value: "7", label: "7 derniers jours" },
  { value: "30", label: "30 derniers jours" },
  { value: "90", label: "3 derniers mois" },
  { value: "365", label: "1 an" },
  { value: "custom", label: "Personnalisé" },
  { value: "all", label: "Tout (sans limite)" },
];

/** Preset limit options */
const LIMIT_PRESETS = [
  { value: 10, label: "10 résultats" },
  { value: 50, label: "50 résultats" },
  { value: 100, label: "100 résultats" },
  { value: 500, label: "500 résultats" },
  { value: 1000, label: "1000 résultats" },
];

/**
 * Modal for configuring tracker execution options
 * Allows users to set limit, period, and other options before running a tracker
 */
const RunTrackerModal: React.FC<RunTrackerModalProps> = ({
  open,
  onClose,
  tracker,
  onConfirm,
  isLoading = false,
}) => {
  // Options state
  const [limit, setLimit] = useState<number>(100);
  const [periodPreset, setPeriodPreset] = useState<string>("30");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [force, setForce] = useState<boolean>(true);
  const [runAsync, setRunAsync] = useState<boolean>(false);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);

  // Get tracker display info
  const trackerValue = tracker ? getTrackerValue(tracker) : null;
  const source = tracker ? getTrackerSource(tracker) : null;
  const displayName = trackerValue?.value || tracker?.id || "Tracker";

  // Calculate period days from preset
  const getPeriodDays = useCallback((): number | undefined => {
    if (periodPreset === "all") return undefined;
    if (periodPreset === "custom") return undefined;
    return parseInt(periodPreset, 10);
  }, [periodPreset]);

  // Handle confirm
  const handleConfirm = useCallback(() => {
    const options: RunTrackerOptions = {
      limit,
      force,
      runAsync,
    };

    // Add period or custom dates
    if (periodPreset === "custom" && fromDate) {
      options.fromDate = fromDate;
      if (toDate) {
        options.toDate = toDate;
      }
    } else if (periodPreset !== "all") {
      options.periodDays = getPeriodDays();
    }

    onConfirm(options);
  }, [limit, force, runAsync, periodPreset, fromDate, toDate, getPeriodDays, onConfirm]);

  // Reset state when modal opens
  React.useEffect(() => {
    if (open) {
      setLimit(100);
      setPeriodPreset("30");
      setFromDate("");
      setToDate("");
      setForce(true);
      setRunAsync(false);
      setShowAdvanced(false);
    }
  }, [open]);

  const isCustomPeriod = periodPreset === "custom";

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      icon={<FaPlay />}
      title="Exécuter le tracker"
      subtitle={displayName}
      variant="accent"
      size="md"
      primaryAction={{
        label: "Exécuter",
        onClick: handleConfirm,
        loading: isLoading,
        icon: <FaRocket className="text-sm" />,
      }}
    >
      <div className="space-y-6">
        {/* Tracker Info Header */}
        <div className="flex items-center gap-3 p-4 bg-surface-secondary rounded-arc-lg border border-arc-border">
          <div className="p-2 bg-accent/10 rounded-arc-md">
            <SourceIcon source={source || ""} size="md" />
          </div>
          <div className="flex-1 min-w-0">
            <Typography variant="text-sm" className="font-medium text-content-primary truncate">
              {displayName}
            </Typography>
            <Typography variant="text-xs" className="text-content-tertiary">
              Source: {source || "N/A"} {tracker?.path ? `• ${tracker.path}` : ""}
            </Typography>
          </div>
        </div>

        {/* Main Options */}
        <div className="space-y-4">
          {/* Limit Selection */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FaListOl className="text-accent text-sm" />
              <Typography variant="text-sm" className="font-medium text-content-primary">
                Nombre de résultats
              </Typography>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {LIMIT_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => setLimit(preset.value)}
                  className={`px-3 py-2 rounded-arc-md text-xs font-medium transition-all ${
                    limit === preset.value
                      ? "bg-accent text-white"
                      : "bg-surface-secondary text-content-secondary hover:bg-surface-elevated hover:text-content-primary border border-arc-border"
                  }`}
                >
                  {preset.value}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-3 mt-2">
              <Slider
                value={limit}
                onChange={(val) => setLimit(val)}
                min={1}
                max={1000}
                step={1}
                className="flex-1"
              />
              <Input
                type="number"
                value={limit}
                onChange={(e) => setLimit(parseInt(e.target.value, 10) || 100)}
                min={1}
                max={10000}
                className="w-24"
                size="sm"
              />
            </div>
          </div>

          {/* Period Selection */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <FaCalendarAlt className="text-accent text-sm" />
              <Typography variant="text-sm" className="font-medium text-content-primary">
                Période
              </Typography>
            </div>
            <Select
              value={periodPreset}
              onChange={(val) => setPeriodPreset(val)}
              className="w-full"
            >
              {PERIOD_PRESETS.map((preset) => (
                <Option key={preset.value} value={preset.value}>
                  {preset.label}
                </Option>
              ))}
            </Select>

            {/* Custom Date Range */}
            {isCustomPeriod && (
              <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-surface-secondary rounded-arc-md border border-arc-border">
                <div>
                  <Typography variant="text-xs" className="text-content-tertiary mb-1">
                    Date de début
                  </Typography>
                  <Input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    size="sm"
                  />
                </div>
                <div>
                  <Typography variant="text-xs" className="text-content-tertiary mb-1">
                    Date de fin
                  </Typography>
                  <Input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    size="sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Advanced Options Toggle */}
        <div className="border-t border-arc-border pt-4">
          <button
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-2 text-sm text-content-secondary hover:text-content-primary transition-colors"
          >
            <FaCog className={`transition-transform ${showAdvanced ? "rotate-90" : ""}`} />
            <span>Options avancées</span>
          </button>

          {showAdvanced && (
            <div className="mt-4 space-y-4 p-4 bg-surface-secondary rounded-arc-md border border-arc-border">
              {/* Force Execution */}
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="text-sm" className="font-medium text-content-primary">
                    Forcer l'exécution
                  </Typography>
                  <Typography variant="text-xs" className="text-content-tertiary">
                    Exécuter même si le tracker a été lancé récemment
                  </Typography>
                </div>
                <Switch
                  checked={force}
                  onChange={setForce}
                />
              </div>

              {/* Async Execution */}
              <div className="flex items-center justify-between">
                <div>
                  <Typography variant="text-sm" className="font-medium text-content-primary">
                    Exécution asynchrone
                  </Typography>
                  <Typography variant="text-xs" className="text-content-tertiary">
                    Lancer en arrière-plan (ne pas attendre la fin)
                  </Typography>
                </div>
                <Switch
                  checked={runAsync}
                  onChange={setRunAsync}
                />
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="p-3 bg-accent/5 rounded-arc-md border border-accent/20">
          <div className="flex items-center gap-2 text-accent">
            <FaClock className="text-sm" />
            <Typography variant="text-sm" className="font-medium">
              Résumé de l'exécution
            </Typography>
          </div>
          <Typography variant="text-xs" className="text-content-secondary mt-1">
            {limit} résultats maximum
            {periodPreset === "all" ? " • Toutes les dates" : 
             periodPreset === "custom" && fromDate ? ` • Du ${fromDate}${toDate ? ` au ${toDate}` : ""}` :
             ` • ${PERIOD_PRESETS.find(p => p.value === periodPreset)?.label}`}
            {force ? " • Exécution forcée" : ""}
            {runAsync ? " • Mode asynchrone" : ""}
          </Typography>
        </div>
      </div>
    </BaseModal>
  );
};

RunTrackerModal.displayName = "RunTrackerModal";

export { RunTrackerModal };
export default RunTrackerModal;
